import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { debounceTime, Subject } from 'rxjs';
import { ClientService } from 'src/app/services/client.service';
import { ContextService } from 'src/app/services/context.service';
import { StripeService } from 'src/app/services/stripe.service';

@Component({
	selector: 'app-welcome-purchase-form',
	templateUrl: './purchase-form.component.html',
	styleUrls: ['./purchase-form.component.css']
})
export class WelcomePurchaseFormComponent implements OnInit {
	@ViewChild('selectBillingType') selectBillingType: ElementRef;
	@ViewChild('inputSignatures') inputSignatures: ElementRef;
	@ViewChild('selectSignatures') selectSignatures: ElementRef;
	@ViewChild('selectStandardOrPremium') selectStandardOrPremium: ElementRef;

	loading = false;
	loadingCalculatePrice = false;

	form: UntypedFormGroup;

	subscriptionType = 'shared'; 	// shared,dedicated,connector
	billingType = 'year';			// year,month

	inputSignaturesValid: boolean;

	cost;

	private readonly debounceTime350Ms = 350;
	private inputSignaturesSubject = new Subject<string>();
	selectedPriceId;

	constructor(
		private fb: UntypedFormBuilder,
		private clientService: ClientService,
		public contextService: ContextService,
		private stripeService: StripeService,
		private router: Router
	) { }

	ngOnInit() {
		this.initForm();
		this.subscribeToInputSignaturesSubject();
	}

	/** FORM FUNCTIONS */

	initForm() {
		this.inputSignaturesValid = false;
		this.cost = null;

		console.log('initForm');
		if (this.subscriptionType == 'shared') {
			this.form = this.fb.group({
				subscriptionType: new UntypedFormControl('shared', [Validators.required]),
				billingType: new UntypedFormControl(this.billingType, [Validators.required]),
				inputSignatures: new UntypedFormControl('', [Validators.required]),
				selectSignatures: new UntypedFormControl('', []),
				standardOrPremium: new UntypedFormControl('standard', []),

			});
		} else if (this.subscriptionType == 'dedicated') {
			this.form = this.fb.group({
				subscriptionType: new UntypedFormControl('dedicated', [Validators.required]),
				billingType: new UntypedFormControl(this.billingType, [Validators.required]),
				inputSignatures: new UntypedFormControl('', []),
				selectSignatures: new UntypedFormControl('', [Validators.required]),
				standardOrPremium: new UntypedFormControl('standard', [Validators.required]),
			});
		} else if (this.subscriptionType == 'connector') {
			this.form = this.fb.group({
				subscriptionType: new UntypedFormControl('connector', [Validators.required]),
				billingType: new UntypedFormControl(this.billingType, [Validators.required]),
				inputSignatures: new UntypedFormControl('', [Validators.required]),
				selectSignatures: new UntypedFormControl('', []),
				standardOrPremium: new UntypedFormControl('standard', []),
			});
		}
	}

	subscribeToInputSignaturesSubject() {
		this.inputSignaturesSubject.pipe(debounceTime(this.debounceTime350Ms)).subscribe(() => {
			this.calculatePrice();
		});
	}

	/** CHANGE LISTENERS */

	changeSubscriptionType(event) {
		this.subscriptionType = event.srcElement.value;

		this.initForm();
	}

	changeBillingType(event) {
		this.billingType = event.srcElement.value;

		if (this.inputSignaturesValid) {
			this.calculatePrice();
		}
	}

	changeInputSignatures(e: any) {
		if (e.target.value) {
			this.inputSignaturesValid = true;

			this.inputSignaturesSubject.next(null);
		} else {
			this.inputSignaturesValid = false;

			this.cost = null;
		}
	}

	changeSelectSignatures(event) {
		this.calculatePrice();
	}

	changeStandardOrPremium(event) {
		this.calculatePrice();
	}

	/** BUTTON ACTIONS */

	selectCreateQuote() {
		this.loading = true;

		let usageType = 'request'; // TODO: Make this dynamic either request/seat

		let units;
		if (this.subscriptionType == 'dedicated') {
			units = this.selectSignatures.nativeElement.value;
			this.billingType = 'year';
		} else {
			units = this.inputSignatures.nativeElement.value;
		}

		this.stripeService.createStripeQuote(this.contextService.stripeCustomerId, this.selectedPriceId, units, this.subscriptionType, this.billingType, usageType)
			.subscribe((quote) => {
				this.retrieveQuotes();
			});
	}

	selectBack() {
		this.router.navigate(['/welcome/type'], { queryParams: { stopNavigation: true, } });
	}

	selectShowQuotes() {
		this.router.navigate(['/welcome/purchase/quotes']);
	}

	/** OTHER FUNCTIONS */

	// Retrieve quotes, format dates, then navigate to quote page
	retrieveQuotes() {
		if (this.contextService.stripeCustomerId) {
			this.stripeService.getStripeQuotes(this.contextService.stripeCustomerId)
				.subscribe((quotes) => {
					this.formatQuoteDates(quotes);
				});
		} else {
			// This will get the customer id
			this.clientService.createClientCustomerId(this.contextService.procoreUserInfo.email, this.contextService.procoreUserInfo.companyName)
				.subscribe((customerId) => {
					this.contextService.stripeCustomerId = customerId;
					this.stripeService.getStripeQuotes(customerId)
						.subscribe((quotes) => {
							this.formatQuoteDates(quotes);
						});
				});
		}
	}

	formatQuoteDates(quotes) {
		for (let quote of quotes) {

			console.log(quote.expiresAt);

			let d = new Date(0);

			let utc = quote.expiresAt;
			d.setUTCSeconds(utc);

			quote.expiresAt = d;
		}

		this.contextService.quotes = quotes;

		this.router.navigate(['/welcome/purchase/quotes']);
	}

	calculatePrice() {
		if (!this.loadingCalculatePrice) {
			this.loadingCalculatePrice = true;

			let billingType = this.selectBillingType.nativeElement.value;

			let units;
			if (this.subscriptionType == 'dedicated') {
				units = this.selectSignatures.nativeElement.value;
			} else {
				units = this.inputSignatures.nativeElement.value;
			}

			let priceId;
			if (this.subscriptionType == 'shared') {
				if (billingType == 'year') {
					priceId = 'price_1Stdy82mrou10v9NRpKRAcVO'; // DBSPC-S-A
				} else {
					priceId = 'price_1SwQrj2mrou10v9N3uk7jwi0'; // DBSPC-S-M
				}
			} else if (this.subscriptionType == 'dedicated') {
				if (this.selectStandardOrPremium.nativeElement.value == 'standard') {
					priceId = 'price_1StdWC2mrou10v9NucdlVDxq'; // DBSPC-D-Standard
				} else {
					priceId = 'price_1StaZ22mrou10v9NUm82eLzx'; // DBSPC-D-Prem
				}
			} else {
				if (billingType == 'year') {
					priceId = 'price_1SwRtM2mrou10v9N9qFr04tQ'; // DBSPC-C-A
				} else {
					priceId = 'price_1SwRtM2mrou10v9N7IdrxmMP'; // DBSPC-C-M
				}
			}

			this.selectedPriceId = priceId;

			this.stripeService.getStripePrice(priceId, units)
				.subscribe((response) => {
					this.cost = response;
					this.loadingCalculatePrice = false;
				});
		}
	}
}